import type { Prisma } from "@prisma/client";
import { taskAbilityControlSelect, tasksSelect, dashboardTasksSelect, brokerTaskSelector } from "@dune/vars";
import { RelatedEntityType } from "../common/common.types";
import type { PaginatedResponse } from "../common/pagination.types";
import { TaskTemplateKeyValues } from "./tasks.catalog";

export enum TaskType {
	PRICING_INDICATION = "PRICING_INDICATION",
	SUBMISSION = "SUBMISSION",
	ANALYSIS = "ANALYSIS",
}

export type ConstructionSiteTaskType = 
	| TaskType.PRICING_INDICATION 
	| TaskType.SUBMISSION;

export enum TaskCTAType {
  ENDPOINT = "ENDPOINT",
  REDIRECT = "REDIRECT",
  MODAL = "MODAL",
}

export interface TaskCTA {
  label: string;
  actionType: TaskCTAType;
  navigationPath?: string;
  modalKey?: string;
  endpoint?: string;
}

export const TaskStatusEnum = {
	TO_DO : "TO_DO",
	IN_PROGRESS : "IN_PROGRESS",
	DONE : "DONE",
} as const;

export type TaskStatus = keyof typeof TaskStatusEnum;

export interface TaskUser {
	id: number;
	firstname: string;
	lastname: string;
	email: string | null;
	role: string;
	subrole?: string;
}

export interface Task {
	id: number;
	templateKey: TaskTemplateKeyValues | null; 
	title: string;
	description: string | null;
	status: TaskStatus;
	assignmentRole: string | null; 
	assigneeId: number | null;
	assignee?: {
		id: number;
		firstname: string;
		lastname: string;
		email: string | null;
	} | null;
	dueDate: Date | null;
	completedAt: Date | null;
	createdAt: Date;
	updatedAt: Date;
	isEditable: boolean;
	relatedEntities: {
		entityType: RelatedEntityType; 
		entityId: number;
	}[];
        files?: {
                file: {
                        id: string;
                        name: string;
                        key: string;
                        mimeType: string;
                };
        }[];
        cta?: TaskCTA;
}

export type TasksList = Task[];

export interface UpdateTaskDto {
	description?: string;
	assigneeId?: number | null;
	status?: TaskStatus;
	title?: string;
	dueDate?: string | Date | null;
}

export interface CreateManualTaskDto {
	title: string;
	description?: string;
	assigneeId?: number | null;
	status?: TaskStatus;
	dueDate?: Date | null;
	relatedEntities: {
		entityType: RelatedEntityType;
		entityId: number;
	}[];
}

export interface CreateManualTaskResponse extends Task {
	assignee?: TaskUser | null;
}

export interface DeleteTaskResponse {
	message: string;
}

export interface UpdateTaskResponse extends Task {
	assignee?: TaskUser | null;
}

export type GetTaskPayload = Prisma.TaskGetPayload<{
	select: typeof tasksSelect;
}>;

export type ConstructionSiteTask = Task;

export type TaskAbilityControl = Prisma.TaskGetPayload<{
	select: typeof taskAbilityControlSelect;
}>;

export type DashboardTask = Prisma.TaskGetPayload<{
	select: typeof dashboardTasksSelect;
}>;

export type DashboardTaskWithEntityReferences = Omit<
	DashboardTask,
	"relatedEntities"
> & {
	relatedEntities: Array<{
		entityType: RelatedEntityType;
		entityId: number;
		entityReference: string | number | null;
	}>;
};

export type BrokerTask = Prisma.TaskGetPayload<typeof brokerTaskSelector> & {
	cta?: TaskCTA | null;
}; 

export interface ActiveTasksForTasksTabResponse
	extends PaginatedResponse<DashboardTaskWithEntityReferences> {
	todoCount: number;
	inProgressCount: number;
}
